import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import bzLogo from '../../assets/img/bugzero/bz-navbar-logo.svg';
import { IcoChev, IcoCheck, IcoGrid, IcoCog } from './icons';
import { PRODUCTS } from './products';

const WorkspaceSwitcher = ({ contextId, platformSection }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const isPlatform = contextId === 'platform';
  const product = !isPlatform ? PRODUCTS.find(p => p.id === contextId) : null;
  const chipLabel = isPlatform ? 'Platform' : product?.short;
  const chipDot = isPlatform ? 'var(--bz-orange-1)' : product?.accent;

  useEffect(() => {
    if (!open) return;
    const onClick = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = path => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div ref={ref} style={{ position: 'relative', padding: 10, borderBottom: '1px solid var(--line-soft)' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 10px',
          borderRadius: 8,
          background: open ? 'var(--bg-soft)' : 'transparent',
          width: '100%',
          boxSizing: 'border-box',
          transition: 'background .12s'
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={bzLogo} alt="BugZero" style={{ height: 28, width: 'auto', display: 'block', flexShrink: 0 }} />
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 600,
              fontSize: 'var(--fs-md)'
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: chipDot }} />
            {chipLabel}
          </span>
        </div>
        <IcoChev
          size={14}
          style={{
            color: 'var(--ink-muted)',
            transform: open ? 'rotate(180deg)' : '',
            transition: 'transform .15s'
          }}
        />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% - 4px)',
            left: 10,
            right: 10,
            background: 'var(--bg-elev)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            boxShadow: 'var(--shadow-pop)',
            padding: 6,
            zIndex: 1050
          }}
        >
          <div
            style={{
              padding: '4px 10px 6px',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '.08em',
              color: 'var(--ink-faint)'
            }}
          >
            PLATFORM
          </div>
          <button
            type="button"
            onClick={() => go('/')}
            style={switcherRowStyle(isPlatform && platformSection === 'home')}
            onMouseEnter={hover(isPlatform && platformSection === 'home')}
            onMouseLeave={leave(isPlatform && platformSection === 'home')}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: 'var(--bg-tan)',
                color: 'var(--bz-orange-1)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <IcoGrid size={15} />
            </div>
            <div className="bz-stack" style={{ flex: 1, lineHeight: 1.2 }}>
              <span style={{ fontWeight: 600, fontSize: 'var(--fs-md)' }}>BugZero Platform</span>
              <span className="bz-muted" style={{ fontSize: 'var(--fs-xs)' }}>
                Home · organization · members · integrations
              </span>
            </div>
            {isPlatform && platformSection === 'home' && <IcoCheck size={14} style={{ color: 'var(--ink-muted)' }} />}
          </button>
          <button
            type="button"
            onClick={() => go('/platform/organization')}
            style={{
              ...switcherSubRowStyle(isPlatform && platformSection !== 'home'),
              padding: '6px 10px 6px 46px'
            }}
            onMouseEnter={hover(isPlatform && platformSection !== 'home')}
            onMouseLeave={leave(isPlatform && platformSection !== 'home')}
          >
            <IcoCog size={12} /> Platform settings
            {isPlatform && platformSection !== 'home' && (
              <IcoCheck size={13} style={{ color: 'var(--ink-muted)', marginLeft: 'auto' }} />
            )}
          </button>

          <div
            style={{
              borderTop: '1px solid var(--line-soft)',
              margin: '6px -6px',
              padding: '6px 6px 0'
            }}
          />

          <div
            style={{
              padding: '0 10px 6px',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '.08em',
              color: 'var(--ink-faint)'
            }}
          >
            PRODUCTS
          </div>
          {PRODUCTS.map(p => {
            const Icon = p.Icon;
            const isCurrent = p.id === contextId;
            return (
              <button
                type="button"
                key={p.id}
                onClick={() => go(p.rootPath)}
                style={switcherRowStyle(isCurrent)}
                onMouseEnter={hover(isCurrent)}
                onMouseLeave={leave(isCurrent)}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: p.accentSoft,
                    color: p.accent,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <Icon size={15} />
                </div>
                <div className="bz-stack" style={{ flex: 1, lineHeight: 1.2 }}>
                  <span style={{ fontWeight: 600, fontSize: 'var(--fs-md)' }}>{p.short}</span>
                  <span className="bz-muted" style={{ fontSize: 'var(--fs-xs)' }}>
                    {p.tag}
                  </span>
                </div>
                {p.status === 'preview' && (
                  <span className="bz-pill bz-pill--predict" style={{ fontSize: 9 }}>
                    SOON
                  </span>
                )}
                {isCurrent && <IcoCheck size={14} style={{ color: 'var(--ink-muted)' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

function switcherRowStyle(active) {
  return {
    all: 'unset',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 6,
    width: '100%',
    boxSizing: 'border-box',
    background: active ? 'var(--bg-soft)' : 'transparent'
  };
}
function switcherSubRowStyle(active) {
  return {
    all: 'unset',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    borderRadius: 6,
    width: '100%',
    boxSizing: 'border-box',
    fontSize: 'var(--fs-sm)',
    color: 'var(--ink-soft)',
    background: active ? 'var(--bg-soft)' : 'transparent'
  };
}
function hover(active) {
  return e => {
    if (!active) e.currentTarget.style.background = 'var(--bg-soft)';
  };
}
function leave(active) {
  return e => {
    if (!active) e.currentTarget.style.background = 'transparent';
  };
}

WorkspaceSwitcher.propTypes = {
  contextId: PropTypes.string.isRequired,
  platformSection: PropTypes.string
};

export default WorkspaceSwitcher;
